import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { logout } from './AuthSlice'
const apiURL = process.env.REACT_APP_BACKEND

/*
 * GateLogSlice — the User Log (session/auth activity) feed shown as a second view
 * under Event Monitor. Mirrors EventSlice: same subscriber/customer scoping + the
 * same infinite-scroll model (append pages on scroll, dedupe by id). No live poll /
 * CSV / column sort. Filters ride `logFilters` ([{id,value}]) the way events use
 * eventFilters: id ∈ search | type | customerId | siteId | gateId | timestamp.
 */
const initialState = {
  logs: [],
  logCount: 0,
  logFilters: [],
  logPage: 1,                    // highest page appended so far (infinite scroll)
  logHasMore: false,
  logListReload: 0,
  logListLoading: false,
  logListLoadingMore: false,
}

export const getGateLogs = createAsyncThunk('gatelog/getGateLogs', async (props, thunkAPI) => {
  const state = thunkAPI.getState()
  const { authId, subscriberId, customerId, webToken } = state.auth
  const filters = (props && props.filters) || {}
  const reqBody = { ...filters }
  // same scoping rule as events: master/super see their subscriber (all for super),
  // everyone else is pinned to their own customer. Super (100) is unscoped server-side, so honor the
  // "All subscribers" dropdown it carries in filters (undefined = every tenant; a value = one tenant);
  // every other role is pinned to its own subscriber (the backend re-enforces this in deriveScope).
  reqBody.subscriberId = (authId === 100) ? reqBody.subscriberId : (parseInt(subscriberId) ? subscriberId : null)
  reqBody.customerId = (!authId || ![100, 1].includes(authId)) ? customerId : reqBody.customerId

  return await fetch(apiURL + 'api/gatelogsNew', {
    method: 'post',
    headers: { 'Authorization': webToken, 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
    credentials: 'include',
  })
    .then((response) => { if (!response.ok) throw Error(response.statusText); return response.json() })
    .catch((error) => { console.error('Error:', error); return { result: [], count: 0 } })
})

const gateLogSlice = createSlice({
  name: 'gatelog',
  initialState,
  reducers: {
    setGateLogState: (state, action) => { state[action.payload.field] = action.payload.value },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGateLogs.fulfilled, (state, action) => {
        const rows = action.payload.result || []
        const count = action.payload.count || 0
        if (action.meta.arg && action.meta.arg.append) {
          // infinite scroll: append the next page, dedupe against what's loaded
          const seen = new Set(state.logs.map((l) => l.id))
          const fresh = rows.filter((l) => !seen.has(l.id))
          state.logs = [...state.logs, ...fresh]
          state.logCount = count
          state.logPage = (action.meta.arg.filters && action.meta.arg.filters.page) || (state.logPage + 1)
          state.logHasMore = state.logs.length < count
        } else {
          // fresh load / filter change: replace from page 1
          state.logs = rows
          state.logCount = count
          state.logPage = 1
          state.logHasMore = rows.length < count
        }
        state.logListLoading = false
        state.logListLoadingMore = false
      })
      .addCase(getGateLogs.rejected, (state) => { state.logListLoading = false; state.logListLoadingMore = false })
      .addCase(logout, () => ({ ...initialState }))
  },
})

export const { setGateLogState } = gateLogSlice.actions
export default gateLogSlice.reducer
